import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { Send, Camera } from "lucide-react";

export default function ClientMessages() {
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !thread) return;
    if (file.size > 2_500_000) { toast.error("Photo too large (max 2.5 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("recipient_id", thread.other.id); fd.append("label", "");
    try {
      const petsResp = await api.get("/pets");
      const myPets = petsResp.data || [];
      if (myPets.length === 1) fd.append("pet_id", myPets[0].id);
      await api.post("/messages/photo", fd);
      toast.success("Photo sent");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const load = async () => {
    const { data } = await api.get("/messages/threads");
    const t = data[0];
    setThread(t);
    if (t) {
      const { data: m } = await api.get(`/messages/${t.other.id}`);
      setMsgs(m);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  const send = async () => {
    if (!body.trim() || !thread) return;
    try {
      await api.post("/messages", { recipient_id: thread.other.id, body: body.trim() });
      setBody("");
      load();
    } catch { toast.error("Send failed"); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <header>
        <div className="label-accent">Chat</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Message your groomer</h1>
      </header>

      <div className="bg-white rounded-3xl border border-[#E5DCCE] shadow-sm flex flex-col h-[65vh]">
        <div className="border-b border-[#E5DCCE] p-4">
          <div className="font-semibold">{thread?.other?.name || "Groomer"}</div>
          <div className="text-xs text-[#6B625B]">Usually replies within an hour</div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="cmsg-scroll">
          {msgs.length === 0 && (
            <p className="text-sm text-[#6B625B] text-center pt-10">Say hi — your groomer is listening.</p>
          )}
          {msgs.map(m => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                  mine ? "bg-[#C85A17] text-white" : "bg-[#F3EDE4] text-[#1F1A17]"
                }`}>
                  {m.photo_url && (
                    <div className="mb-2 -mx-1 -mt-1">
                      {m.photo_label && (
                        <div className={`inline-block mb-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          m.photo_label === "before"
                            ? "bg-amber-200 text-amber-900"
                            : "bg-emerald-200 text-emerald-900"
                        }`}>{m.photo_label}</div>
                      )}
                      <img src={`${API}${m.photo_url.replace('/api','')}`} alt={m.photo_label || "attachment"}
                        className="rounded-xl max-w-full max-h-64 object-cover"/>
                    </div>
                  )}
                  {m.body && <div>{m.body}</div>}
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#6B625B]"}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>
        <div className="border-t border-[#E5DCCE] p-3 flex gap-2">
          <input type="file" accept="image/*" capture="environment" ref={fileRef}
            onChange={uploadPhoto} className="hidden" data-testid="cmsg-photo-file"/>
          <Button variant="outline" onClick={() => fileRef.current?.click()}
            className="rounded-full h-11 w-11 p-0 border-[#E5DCCE]" data-testid="btn-cphoto">
            <Camera className="w-4 h-4"/>
          </Button>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message…" className="min-h-[52px] resize-none" data-testid="cmsg-input"/>
          <Button onClick={send} className="rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="btn-csend">
            <Send className="w-4 h-4"/>
          </Button>
        </div>
      </div>
    </div>
  );
}
